import React, { useState, useEffect, useMemo } from 'react';
import { UserCheck, Plus, Edit2, Trash2, X, ArrowUp, ArrowDown, Check } from 'lucide-react';
import {
  Badge, emptyMsg, cardWrap, neuShadowSm,
  btnPrimary, btnSm, inp,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   LeadAssignmentTab — Foundation #6 admin UI for crm_lead_assignment_rules.
   • Lists active and inactive rules in priority order.
   • Create / edit / delete rules.
   • Three strategies: specific_user, round_robin, least_loaded.
   • Filters by contact_type and lead_source substring (free-form).
   • Round-robin shows "next up" indicator from last_assigned_user_id.
   ═══════════════════════════════════════════════════════════════════════════ */

const STRATEGY_LABELS = {
  specific_user: 'Specific user',
  round_robin: 'Round robin',
  least_loaded: 'Least loaded',
};

const STRATEGY_DESCRIPTIONS = {
  specific_user: 'Always assign new matching leads to one named user.',
  round_robin: 'Cycle through a pool of users in order, one per new lead.',
  least_loaded: 'Pick the user in the pool with the fewest open contacts.',
};

const CONTACT_TYPES = [
  { v: '', label: 'Any type' },
  { v: 'prospect', label: 'Prospect' },
  { v: 'referral_physician', label: 'Referral physician' },
  { v: 'insurance_rep', label: 'Insurance rep' },
  { v: 'vendor', label: 'Vendor' },
  { v: 'partner', label: 'Partner' },
  { v: 'other', label: 'Other' },
];

export default function LeadAssignmentTab({ canCreate, allUsers }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.getLeadAssignmentRules();
      setRules(Array.isArray(r) ? r : []);
    } catch (err) {
      console.error('Lead assignment rules load failed', err);
      setRules([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Build a quick {id: display_name} lookup so we can render pool members.
  const userById = useMemo(() => {
    const m = {};
    for (const u of allUsers || []) m[u.id] = u.display_name || u.username;
    return m;
  }, [allUsers]);

  async function toggleActive(rule) {
    await window.zignagenixAPI.updateLeadAssignmentRule(rule.id, {
      name: rule.name,
      description: rule.description,
      priority: rule.priority,
      isActive: !rule.is_active,
      filterContactType: rule.filter_contact_type,
      filterLeadSource: rule.filter_lead_source,
      assignmentStrategy: rule.assignment_strategy,
      targetUserId: rule.target_user_id,
      targetUserPool: normalizePool(rule.target_user_pool),
    });
    load();
  }

  async function deleteRule(rule) {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    await window.zignagenixAPI.deleteLeadAssignmentRule(rule.id);
    load();
  }

  return (
    <>
      {/* ═══ Header ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <UserCheck size={16} color="#2E8B98" />
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>Lead Assignment</h2>
          </div>
          <div style={{ fontSize: 12, color: '#6C757D', maxWidth: 640 }}>
            Auto-assign new leads to the right rep when they're created. Rules run in priority order;
            first match wins. If no rule matches, the lead defaults to the creator (manual entry) or
            stays unassigned (GHL inbound).
          </div>
        </div>
        {canCreate && (
          <button onClick={() => setShowAdd(true)} style={btnPrimary}>
            <Plus size={14} /> New Rule
          </button>
        )}
      </div>

      {/* ═══ Rules list ═══ */}
      {loading ? (
        <div style={emptyMsg}>Loading rules…</div>
      ) : rules.length === 0 ? (
        <div style={{ ...cardWrap, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#6C757D', marginBottom: 12 }}>
            No assignment rules yet.
          </div>
          <div style={{ fontSize: 11, color: '#ADB5BD', marginBottom: 16 }}>
            Without a rule, manually-created leads default to whoever creates them, and GHL inbound
            leads stay unassigned. Add a round-robin or least-loaded rule to spread leads evenly
            across your team.
          </div>
          {canCreate && (
            <button onClick={() => setShowAdd(true)} style={btnPrimary}>
              <Plus size={14} /> Create your first rule
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rules.map(rule => (
            <RuleRow
              key={rule.id}
              rule={rule}
              userById={userById}
              canCreate={canCreate}
              onEdit={() => setEditing(rule)}
              onDelete={() => deleteRule(rule)}
              onToggleActive={() => toggleActive(rule)}
            />
          ))}
        </div>
      )}

      {/* ═══ Modals ═══ */}
      {showAdd && (
        <RuleModal
          mode="create"
          allUsers={allUsers}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {editing && (
        <RuleModal
          mode="edit"
          rule={editing}
          allUsers={allUsers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

/* ─── Rule row ─────────────────────────────────────────────────────────────── */

function RuleRow({ rule, userById, canCreate, onEdit, onDelete, onToggleActive }) {
  const pool = normalizePool(rule.target_user_pool);
  const strategy = STRATEGY_LABELS[rule.assignment_strategy] || rule.assignment_strategy;

  return (
    <div style={{ ...cardWrap, padding: 14, opacity: rule.is_active ? 1 : 0.55 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          width: 26, height: 26, borderRadius: 6, background: rule.is_active ? '#DCFCE7' : '#F1F5F9',
          color: rule.is_active ? '#15803D' : '#64748B',
          fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {rule.priority}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>{rule.name}</div>
          {rule.description && (
            <div style={{ fontSize: 11, color: '#6C757D' }}>{rule.description}</div>
          )}
        </div>
        <Badge text={strategy} color={rule.is_active ? '#2E8B98' : '#94A3B8'} />
        {!rule.is_active && <Badge text="Disabled" color="#94A3B8" />}
        {canCreate && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={onToggleActive} title={rule.is_active ? 'Disable' : 'Enable'} style={iconBtn}>
              <Check size={13} color={rule.is_active ? '#15803D' : '#94A3B8'} />
            </button>
            <button onClick={onEdit} title="Edit" style={iconBtn}>
              <Edit2 size={13} color="#2E8B98" />
            </button>
            <button onClick={onDelete} title="Delete" style={iconBtn}>
              <Trash2 size={13} color="#DC3545" />
            </button>
          </div>
        )}
      </div>

      {/* Filters + target */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 11 }}>
        <div>
          <div style={metaLabel}>Match contact type</div>
          <div style={metaValue}>{rule.filter_contact_type || 'Any'}</div>
        </div>
        <div>
          <div style={metaLabel}>Match lead source contains</div>
          <div style={metaValue}>{rule.filter_lead_source || 'Any'}</div>
        </div>
        <div>
          <div style={metaLabel}>Assigns to</div>
          <div style={metaValue}>
            {rule.assignment_strategy === 'specific_user'
              ? (rule.target_user_name || userById[rule.target_user_id] || `User #${rule.target_user_id}`)
              : pool.length === 0
                ? '(no users in pool)'
                : pool.map(uid => userById[uid] || `#${uid}`).join(' → ')}
          </div>
          {rule.assignment_strategy === 'round_robin' && rule.last_assigned_user_id && pool.length > 0 && (
            <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>
              Next up: {(() => {
                const idx = pool.findIndex(u => u === rule.last_assigned_user_id);
                const next = pool[(idx + 1) % pool.length];
                return userById[next] || `#${next}`;
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Create / edit modal ──────────────────────────────────────────────────── */

function RuleModal({ mode, rule, allUsers, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: rule?.name || '',
    description: rule?.description || '',
    priority: rule?.priority ?? 0,
    isActive: rule ? rule.is_active : true,
    filterContactType: rule?.filter_contact_type || '',
    filterLeadSource: rule?.filter_lead_source || '',
    assignmentStrategy: rule?.assignment_strategy || 'round_robin',
    targetUserId: rule?.target_user_id || '',
    targetUserPool: normalizePool(rule?.target_user_pool),
  }));
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name) { alert('Name is required'); return; }
    if (form.assignmentStrategy === 'specific_user' && !form.targetUserId) {
      alert('Pick a target user for "Specific user" strategy.'); return;
    }
    if (form.assignmentStrategy !== 'specific_user' && form.targetUserPool.length === 0) {
      alert('Add at least one user to the pool for round-robin / least-loaded strategies.'); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        priority: parseInt(form.priority, 10) || 0,
        isActive: !!form.isActive,
        filterContactType: form.filterContactType || null,
        filterLeadSource: form.filterLeadSource || null,
        assignmentStrategy: form.assignmentStrategy,
        targetUserId: form.assignmentStrategy === 'specific_user'
          ? parseInt(form.targetUserId, 10) || null
          : null,
        targetUserPool: form.assignmentStrategy === 'specific_user'
          ? []
          : form.targetUserPool.map(u => parseInt(u, 10)).filter(Number.isFinite),
      };
      if (mode === 'create') {
        await window.zignagenixAPI.createLeadAssignmentRule(payload);
      } else {
        await window.zignagenixAPI.updateLeadAssignmentRule(rule.id, payload);
      }
      onSaved();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  function togglePoolUser(userId) {
    setForm(f => {
      const has = f.targetUserPool.includes(userId);
      return {
        ...f,
        targetUserPool: has
          ? f.targetUserPool.filter(u => u !== userId)
          : [...f.targetUserPool, userId],
      };
    });
  }

  function movePoolUser(idx, dir) {
    setForm(f => {
      const arr = [...f.targetUserPool];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return f;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return { ...f, targetUserPool: arr };
    });
  }

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 560 }}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>{mode === 'create' ? 'New Assignment Rule' : 'Edit Rule'}</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <form onSubmit={submit}>
          {/* Name + priority */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={lbl}>Name *</label>
              <input value={form.name} onChange={e => up('name', e.target.value)} required style={inp}
                placeholder="e.g. Round-robin sales reps" /></div>
            <div><label style={lbl}>Priority (higher first)</label>
              <input type="number" value={form.priority} onChange={e => up('priority', e.target.value)} style={inp} /></div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Description (optional)</label>
            <input value={form.description} onChange={e => up('description', e.target.value)} style={inp}
              placeholder="What this rule does, why it exists" />
          </div>

          {/* Filters */}
          <div style={{ ...cardWrap, padding: 10, marginBottom: 12, background: '#FAFBFC' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 6 }}>
              Match leads where…
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={lbl}>Contact type</label>
                <select value={form.filterContactType} onChange={e => up('filterContactType', e.target.value)} style={inp}>
                  {CONTACT_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select></div>
              <div><label style={lbl}>Lead source contains</label>
                <input value={form.filterLeadSource} onChange={e => up('filterLeadSource', e.target.value)} style={inp}
                  placeholder="e.g. facebook, podcast" /></div>
            </div>
            <div style={{ fontSize: 10, color: '#6C757D', marginTop: 6 }}>
              Both filters are AND'd. Leave blank to match any.
            </div>
          </div>

          {/* Strategy */}
          <div style={{ ...cardWrap, padding: 10, marginBottom: 12, background: '#FAFBFC' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 6 }}>
              Then assign…
            </div>
            <div style={{ marginBottom: 8 }}>
              <select value={form.assignmentStrategy} onChange={e => up('assignmentStrategy', e.target.value)} style={inp}>
                {Object.entries(STRATEGY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <div style={{ fontSize: 10, color: '#6C757D', marginTop: 4 }}>
                {STRATEGY_DESCRIPTIONS[form.assignmentStrategy]}
              </div>
            </div>

            {form.assignmentStrategy === 'specific_user' ? (
              <div>
                <label style={lbl}>Target user</label>
                <select value={form.targetUserId} onChange={e => up('targetUserId', e.target.value)} style={inp}>
                  <option value="">— select —</option>
                  {(allUsers || []).map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label style={lbl}>User pool (round-robin uses this order)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {(allUsers || []).map(u => {
                    const inPool = form.targetUserPool.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => togglePoolUser(u.id)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 999,
                          border: 'none',
                          background: inPool ? '#2E8B98' : '#F1F5F9',
                          color: inPool ? '#fff' : '#475569',
                          fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        {u.display_name || u.username}{inPool && ' ✓'}
                      </button>
                    );
                  })}
                </div>
                {form.targetUserPool.length > 0 && (
                  <div style={{ ...cardWrap, padding: 8, background: '#fff' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', marginBottom: 4 }}>
                      Order ({form.targetUserPool.length} user{form.targetUserPool.length !== 1 ? 's' : ''}):
                    </div>
                    {form.targetUserPool.map((uid, i) => {
                      const u = (allUsers || []).find(x => x.id === uid);
                      return (
                        <div key={uid} style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
                          borderBottom: i < form.targetUserPool.length - 1 ? '1px solid #F3F4F6' : 'none',
                        }}>
                          <span style={{ fontSize: 11, color: '#475569', flex: 1 }}>
                            {i + 1}. {u?.display_name || u?.username || `#${uid}`}
                          </span>
                          <button type="button" onClick={() => movePoolUser(i, -1)} disabled={i === 0} style={iconBtn}>
                            <ArrowUp size={12} color={i === 0 ? '#CBD5E1' : '#475569'} />
                          </button>
                          <button type="button" onClick={() => movePoolUser(i, 1)}
                            disabled={i === form.targetUserPool.length - 1} style={iconBtn}>
                            <ArrowDown size={12} color={i === form.targetUserPool.length - 1 ? '#CBD5E1' : '#475569'} />
                          </button>
                          <button type="button" onClick={() => togglePoolUser(uid)} style={iconBtn}>
                            <X size={12} color="#DC3545" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Active toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => up('isActive', e.target.checked)} />
            <span style={{ fontSize: 12, color: '#212529' }}>Active</span>
          </label>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" disabled={saving} style={btnPrimary}>
              {saving ? 'Saving…' : (mode === 'create' ? 'Create Rule' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function normalizePool(raw) {
  if (Array.isArray(raw)) return raw.map(n => parseInt(n, 10)).filter(Number.isFinite);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed.map(n => parseInt(n, 10)).filter(Number.isFinite) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const iconBtn = {
  background: '#F8F9FA', border: 'none', cursor: 'pointer',
  padding: 4, borderRadius: 6, boxShadow: neuShadowSm,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const lbl = { fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 };

const metaLabel = { fontSize: 9, fontWeight: 700, color: '#ADB5BD', textTransform: 'uppercase', marginBottom: 2 };
const metaValue = { fontSize: 11, color: '#212529', fontWeight: 600 };
